/**
 * Every external claim in the report points at one of these. Anything that is
 * not measured from our own record needs an entry here — if a statement cannot
 * be sourced or measured, it does not belong in the report.
 */
export interface Source {
  id: string;
  publisher: string;
  title: string;
  url: string;
  note?: string;
}

export const SOURCES: Source[] = [
  {
    id: 'me-interconnection',
    publisher: 'Maritime Electric',
    title: 'Interconnection Upgrade Project',
    url: 'https://www.maritimeelectric.com/about-us/projects/interconnection-upgrade-project/',
    note: 'Two 180 MW submarine cables commissioned 2016/17, complementing two legacy 100 MW interconnects.',
  },
  {
    id: 'me-capacity-filing',
    publisher: 'Maritime Electric / IRAC',
    title: 'On-Island Capacity for Security of Supply Project filing (Dec 2024)',
    url: 'https://www.maritimeelectric.com/media/j02ba1vx/scbr-request-for-the-on-island-capacity-for-security-of-supply-project-filed-december-18-2024.pdf',
    note: 'Physical cable capacity is 560 MW but interconnection is limited to 300 MW by New Brunswick transmission constraints.',
  },
  {
    id: 'cer-pei',
    publisher: 'Canada Energy Regulator',
    title: 'Provincial and Territorial Energy Profiles — Prince Edward Island',
    url: 'https://www.cer-rec.gc.ca/en/data-analysis/energy-markets/provincial-territorial-energy-profiles/provincial-territorial-energy-profiles-prince-edward-island.html',
    note: 'Reports PEI sourcing approximately 69% of its electricity from New Brunswick (2023).',
  },
  {
    id: 'atlantica-pei',
    publisher: 'Atlantica Centre for Energy',
    title: "Prince Edward Island's Energy Resources",
    url: 'https://www.atlanticaenergy.org/energy-knowledge-centre/energy-maps/prince-edward-islands-energy-resources/',
    note: 'Reports PEI importing about 85% of its electricity from New Brunswick.',
  },
  {
    id: 'pembina-wind',
    publisher: 'Pembina Institute',
    title: 'Prince Edward Island rocks wind power',
    url: 'https://www.pembina.org/blog/prince-edward-island-rocks-wind-power',
    note: 'Engie (formerly GDF Suez) owns 108 MW of PEI wind and sells most of it to New Brunswick under contract; almost half of PEI wind is contracted off-island.',
  },
  {
    id: 'pei-energy-strategy',
    publisher: 'Government of Prince Edward Island',
    title: 'PEI Energy Strategy',
    url: 'https://www.princeedwardisland.ca/sites/default/files/49be/PEI%20Energy%20Strategy%20Summary.pdf',
    note: 'Peak load has exceeded 400 MW and occurs in the winter heating season, driven partly by growth in electric heat and heat pumps.',
  },
  {
    id: 'me-outage-map',
    publisher: 'Maritime Electric',
    title: 'Public outage feed (ArcGIS FeatureServer)',
    url: 'https://www.maritimeelectric.com/outages/outage-map/',
    note: 'Source of every outage and affected-community figure in this report.',
  },
  {
    id: 'gov-pei-renewable',
    publisher: 'Government of Prince Edward Island',
    title: 'Renewable Energy in Prince Edward Island',
    url: 'https://www.princeedwardisland.ca/en/information/transportation-infrastructure-and-energy/renewable-energy-in-prince-edward-island',
    note: 'Publisher of the live generation, load and renewable-distribution gauges this report aggregates.',
  },
  {
    id: 'eccc',
    publisher: 'Environment and Climate Change Canada',
    title: 'MSC GeoMet weather observations',
    url: 'https://eccc-msc.github.io/open-data/msc-geomet/readme_en/',
    note: 'Temperature, wind, gust and pressure for Charlottetown, Summerside, Souris and Tignish.',
  },
  {
    id: 'cbc-two-utilities',
    publisher: 'CBC News',
    title: "Why does P.E.I. have 2 electric utilities, and why are they at odds?",
    url: 'https://www.cbc.ca/news/canada/prince-edward-island/pei-electricity-explainer-1.7459199',
    note: 'Summerside has run its own municipal electric utility since 1920, serving about 7,000 customers. Maritime Electric serves the rest of the Island, and some homes inside Summerside sit on its system.',
  },
  {
    id: 'me-net-metering',
    publisher: 'Maritime Electric',
    title: 'Net Metering',
    url: 'https://www.maritimeelectric.com/services/services/net-metering/',
    note: 'Customer-owned wind and solar is credited against the owner’s own consumption rather than metered as supply to the grid. More than 1,000 Island customers participate.',
  },
];

export const SOURCE_INDEX: Record<string, number> = Object.fromEntries(
  SOURCES.map((s, i) => [s.id, i + 1]),
);
